import { Trans, useTranslation } from 'next-i18next';
import Image from 'next/image';
import { type FC, useCallback, useEffect, useState } from 'react';

import { Flex } from '../landingV4/common/Flex';
import { TextLink } from '../landingV4/common/TextLink';

import { COOKIE_BANNER_DESKTOP_MEDIA_QUERY } from './constants';
import styles from './CookieConsentBanner.module.scss';
import {
  dispatchCookieConsentChanged,
  getEffectiveCookieConsentPreferences,
  IMPLICIT_COOKIE_CONSENT_FULL,
  persistCookieConsent,
  pushConsentToGtag,
  readStoredCookieConsent,
  type CookieConsentPreferences,
} from './cookieConsentStorage';
import { CookieSettingsDialog } from './CookieSettingsDialog';

const rejectPreferences: CookieConsentPreferences = {
  analytics: false,
  marketing: false,
};

/** Copy so we never mutate the exported implicit-default object. */
const acceptAllPreferences: CookieConsentPreferences = { ...IMPLICIT_COOKIE_CONSENT_FULL };

export const CookieConsentBanner: FC = () => {
  const { t } = useTranslation('common');
  const [showBanner, setShowBanner] = useState(false);
  const [showDecoration, setShowDecoration] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(COOKIE_BANNER_DESKTOP_MEDIA_QUERY).matches
  );
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsInitial, setSettingsInitial] = useState<CookieConsentPreferences>(IMPLICIT_COOKIE_CONSENT_FULL);

  useEffect(() => {
    const mq = window.matchMedia(COOKIE_BANNER_DESKTOP_MEDIA_QUERY);
    const sync = () => setShowDecoration(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    const stored = readStoredCookieConsent();
    if (!stored) {
      setShowBanner(true);
    }
  }, []);

  useEffect(() => {
    if (!showBanner) {
      setSettingsOpen(false);
    }
  }, [showBanner]);

  const commitConsent = useCallback((prefs: CookieConsentPreferences) => {
    persistCookieConsent(prefs);
    dispatchCookieConsentChanged(prefs);
    pushConsentToGtag(prefs);
    setShowBanner(false);
  }, []);

  const handleAcceptAll = useCallback(() => {
    commitConsent(acceptAllPreferences);
  }, [commitConsent]);

  const handleRejectAll = useCallback(() => {
    commitConsent(rejectPreferences);
  }, [commitConsent]);

  const handleOpenSettings = useCallback(() => {
    setSettingsInitial(getEffectiveCookieConsentPreferences());
    setSettingsOpen(true);
  }, []);

  return (
    <>
      {showBanner ? (
        <aside className={styles.root} role="dialog" aria-label={t('cookieConsent.ariaLabel')} aria-live="polite">
          {showDecoration ? (
            <div className={styles.background} aria-hidden>
              <div className={styles.bgSliceLeft}>
                <Image
                  src="/assets/cookie-consent-bg.svg"
                  alt=""
                  layout="fill"
                  objectFit="cover"
                  objectPosition="left bottom"
                  unoptimized
                />
              </div>
              <div className={styles.bgSliceRight}>
                <Image
                  src="/assets/cookie-consent-bg.svg"
                  alt=""
                  layout="fill"
                  objectFit="cover"
                  objectPosition="right bottom"
                  unoptimized
                />
              </div>
            </div>
          ) : null}

          <div className={styles.inner}>
            <Flex className={styles.copy} direction="column" gap={6}>
              <p className={styles.message}>
                <Trans
                  i18nKey="cookieConsent.message"
                  components={{
                    siteLink: (
                      <TextLink className={styles.inlineLink} href="/" variant="white-50p" type="regular14">
                        {''}
                      </TextLink>
                    ),
                    cookiesLink: (
                      <TextLink
                        className={styles.inlineLink}
                        href="/doc/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="white-50p"
                        type="regular14"
                      >
                        {''}
                      </TextLink>
                    ),
                  }}
                />
              </p>

              <p className={styles.privacyLine}>
                <Trans
                  i18nKey="cookieConsent.privacyLine"
                  components={{
                    privacyLink: (
                      <TextLink
                        href="/doc/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="white-50p"
                        type="regular14"
                      >
                        {''}
                      </TextLink>
                    ),
                  }}
                />
              </p>
            </Flex>

            <div className={styles.actions}>
              <button type="button" className={styles.btnAccept} onClick={handleAcceptAll}>
                {t('cookieConsent.acceptAll')}
              </button>

              <button type="button" className={styles.btnSecondary} onClick={handleRejectAll}>
                {t('cookieConsent.rejectAll')}
              </button>

              <button type="button" className={styles.btnSecondary} onClick={handleOpenSettings}>
                {t('cookieConsent.cookieSettings')}
              </button>
            </div>
          </div>
        </aside>
      ) : null}

      <CookieSettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        initialPreferences={settingsInitial}
        onSave={commitConsent}
      />
    </>
  );
};
